import React, { useState, useEffect } from 'react'
import { Redirect } from 'react-router-dom'
import FreelancerCreateForm from './FreelancerCreateForm'
import FreelancerWebsiteForm from './FreelancerWebsiteForm'
import FreelancerSelectCity from './FreelancerSelectCity'
import FreelancerSearchCity from './FreelancerSearchCity'
import FreelancerLinksForm from './FreelancerLinksForm'
import FreelancerSelectOntarioWorkingStatus from './FreelancerSelectOntarioWorkingStatus'
import FreelancerCanadaWorkStatus from './FreelancerCanadaWorkStatus'
import FreelancerSelectRelocationStatus from './FreelancerSelectRelocationStatus'
import FreelancerSelectDesiredCommitment from './FreelancerSelectDesiredCommitment'
import FreelancerCredits from '../FreelancerCredits'
import FreelancerUploadCv from './FreelancerUploadCv'
import FreelancerDiversityTags from '../FreelancerDiversityTags'
import FreelancerSelfIdentification from '../FreelancerSelfIdentification'
import FreelancerGenderTags from '../FreelancerGenderTags'
import FreelancerAvatar from '../FreelancerCreate/FreelancerAvatar'
import FreelancerSelectEmploymentStatus from './FreelancerSelectEmploymentStatus'
import FreelancerSelectCrewOrPerformer from './FreelancerSelectCrewOrPerformer'
import FreelancerPrimaryRole from './FreelancerPrimaryRole'
import FreelancerManageRoles from './FreelancerManageRoles'
import FreelancerSkillTags from './FreelancerSkillTags'
import FreelancerRoles from '../FreelancerRoles'
import { FreelancerAddRole } from '../FreelancerRoles/FreelancerAddRole'
import FreelancerEquipment from '../FreelancerEquipment'
import IntroDialog from '../FreelancerMonth/IntroDialog'
import FreelancerUnionStatus from './FreelancerUnionStatus'
import FreelancerDisabilitySelect from './FreelancerDisabilitySelect'
import FreelancerProgramSelect from './FreelancerProgramSelect'
import FreelancerLanguageSelect from './FreelancerLanguageSelect'
import FreelancerEquipmentSelect from './FreelancerEquipmentSelect'
import FreelancerPerformerTypeSelect from './FreelancerPerformerTypeSelect'
import FreelancerPerformerAppearance from './FreelancerPerformerAppearance'
import { useOnboardConfig, useTags } from 'utils'
import {
  ONTARIO_WORKING_STATUS,
  ONTARIO_RESIDENCE_STATUS,
  COMMITMENT_STATUS,
  CREDITS_ENABLED,
  DIVERSITY_TAGS_ENABLED,
  CV_ONBOARD_ENABLED,
  GENDER_TAGS_ENABLED,
  PROFILE_PICTURES_ENABLED,
  USE_DEMO_LANDER,
  NUM_ROLE_STEPS,
} from 'constants'

const FreelancerCreateView = ({ cities, user, markets, createRole }) => {
  const [stepIndex, setStepIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  // Get the onboard configuration
  const onboardConfigHook = useOnboardConfig()
  const tags = useTags()

  // For debugging the hook structure
  useEffect(() => {
    // console.log('FreelancerCreate - onboardConfigHook received:', onboardConfigHook)

    // Based on the log, onboardConfigHook returns { onboardConfig: [...], isStepEnabled: function }
    if (onboardConfigHook && onboardConfigHook.onboardConfig && onboardConfigHook.onboardConfig.length > 0) {
      // console.log('FreelancerCreate - Steps loaded:', onboardConfigHook.onboardConfig)
      setIsLoading(false)
    }
  }, [onboardConfigHook])

  if (!user) {
    return <FreelancerCreateForm />
  }

  // Show loading state while waiting for onboardConfig to load
  if (isLoading) {
    return (
      <div style={{ padding: 20, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100px' }}>
        <div style={{
          border: '4px solid rgba(0, 0, 0, 0.1)',
          borderTopColor: '#007bff',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 1s linear infinite',
        }}>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    )
  }

  // Get the onboarding steps directly from the hook
  // Based on the log, onboardConfigHook.onboardConfig is already the array of steps
  const onboardingSteps = onboardConfigHook.onboardConfig || []

  // console.log('Onboarding steps:', onboardingSteps)

  // If no steps are configured, show an error
  if (onboardingSteps.length === 0) {
    // console.error('No onboarding steps are enabled in configuration')
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <h3>Error: No onboarding steps configured</h3>
        <p>Please contact support.</p>
      </div>
    )
  }

  // Safety check for step index
  if (stepIndex >= onboardingSteps.length) {
    return <Redirect to="/profile" />
  }

  // Define the total steps and current step
  const totalSteps = onboardingSteps.length
  const currentStepName = onboardingSteps[stepIndex].name

  // console.log('FreelancerCreate - Using step:', currentStepName, 'at index', stepIndex)
  // console.log('Processing step with name:', currentStepName)

  switch (currentStepName) {
    case 'website':
      return <FreelancerWebsiteForm user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps} onNext={() => {
        setStepIndex(stepIndex + 1)
      }} />
    case 'linksForm':
      return <FreelancerLinksForm onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps} onNext={() => {
        setStepIndex(stepIndex + 1)
      }} />
    case 'city':
      return <FreelancerSelectCity onboarding cities={cities} user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'searchCity':
      return <FreelancerSearchCity onboarding cities={cities} user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'ontarioWorkingStatus':
      return <FreelancerSelectOntarioWorkingStatus onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'canadaWorkStatus':
      return <FreelancerCanadaWorkStatus onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'relocationStatus':
      return <FreelancerSelectRelocationStatus onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'desiredCommitment':
      return <FreelancerSelectDesiredCommitment onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'genderTags':
      return <FreelancerGenderTags onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'diversityTags':
      return <FreelancerDiversityTags onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'selfIdentification':
      return <FreelancerSelfIdentification onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'credits':
      return <FreelancerCredits onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'crewOrPerformer':
      return <FreelancerSelectCrewOrPerformer onboarding user={user} markets={markets} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'primaryRole':
      return <FreelancerPrimaryRole onboarding user={user} markets={markets} tags={tags} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'skillTags':
      return <FreelancerSkillTags onboarding user={user} tags={tags} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'cv':
      return <FreelancerUploadCv onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'employmentStatus':
      return <FreelancerSelectEmploymentStatus onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'profilePicture':
      return <FreelancerAvatar onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'roles':
      return <FreelancerAddRole
        onboarding
        user={user}
        stepIndex={stepIndex + 1}
        totalSteps={totalSteps}
        markets={markets}
        createRole={createRole}
        tags={tags}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'equipment':
      return <FreelancerEquipment onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'canadaWorkStatus':
      return <FreelancerCanadaWorkStatus onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'unionStatus':
      return <FreelancerUnionStatus onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'disabilitySelect':
      return <FreelancerDisabilitySelect onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'programSelect':
      return <FreelancerProgramSelect onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'languages':
      return <FreelancerLanguageSelect onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    case 'listGear':
      return <FreelancerEquipmentSelect onboarding user={user} stepIndex={stepIndex + 1} totalSteps={totalSteps}
        onNext={() => {
          setStepIndex(stepIndex + 1)
        }}
        onSubmit={() => {
          setStepIndex(stepIndex + 1)
        }} />
    default:
      // Log unmatched step name for debugging
      console.log(`Unmatched step name: '${currentStepName}'. ` +
        `Valid step names are: website, linksForm, city, searchCity, ontarioWorkingStatus, ` +
        `canadaWorkStatus, relocationStatus, desiredCommitment, genderTags, diversityTags, ` +
        `selfIdentification, credits, crewOrPerformer, primaryRole, skillTags, cv, ` +
        `employmentStatus, profilePicture, roles, equipment, canadaWorkStatus, unionStatus, ` +
        `disabilitySelect, programSelect, languages, listGear`);

      // Try to find a closely matching component to render as a fallback
      // Alternatively, move to the next step if one exists
      if (stepIndex + 1 < onboardingSteps.length) {
        console.log('Moving to next step instead of redirecting')
        setStepIndex(stepIndex + 1);
        return null; // Re-render with updated stepIndex
      } else {
        // If no more steps, redirect to profile
        console.log('No more steps available, redirecting to profile')
        return <Redirect to="/profile?source=onboarding" />;
      }
  }
}

export default FreelancerCreateView
